import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex, Text, Icon, Stack, Button, Spinner, HStack } from "@chakra-ui/react";
import { Flag, Link2, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { cachePut, cacheGet, safeQuery } from "@/lib/offline";
import { urgentOrFilter, ago, resolveSubmission } from "@/lib/urgent";
import { showToast } from "@/components/AppToaster";
import { colors } from "@/theme/colors";

const CHIPS = [
  { key: "all", label: "All" },
  { key: "flagged", label: "Flagged reports" },
  { key: "actions", label: "Action requests" },
];

// Dedicated triage queue: EVERY open urgent item (uncapped), same criteria
// as the alerts bell — pending status AND (auto-flagged OR raised action type).
export default function UrgentQueue() {
  const navigate = useNavigate();
  const { user, activeCompanyId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);
  const [chip, setChip] = useState("all");
  const [oldestFirst, setOldestFirst] = useState(true);
  const [seen, setSeen] = useState(() => new Set());

  const readSeen = useCallback(() => {
    const saved = cacheGet(user.id, "bell_seen");
    setSeen(new Set(Array.isArray(saved) ? saved : []));
  }, [user.id]);

  const load = useCallback(async () => {
    readSeen();
    const { data, error } = await safeQuery(supabase.from("submissions")
      .select("id,template_title,template_type,worker_name,location_name,created_at,flagged,flag_reasons,link_context")
      .eq("company_id", activeCompanyId).eq("status", "pending")
      .or(urgentOrFilter())
      .order("created_at", { ascending: false }).limit(1000));
    if (error || data === null) {
      const snap = cacheGet(user.id, "urgent_queue");
      setRows(Array.isArray(snap) ? snap : []);
      setOffline(true);
    } else {
      setRows(data || []);
      setOffline(false);
      cachePut(user.id, "urgent_queue", (data || []).slice(0, 200));
    }
    setLoading(false);
  }, [activeCompanyId, user.id, readSeen]);

  useEffect(() => {
    load();
    const onRefresh = () => load();
    const onViewed = () => readSeen();
    window.addEventListener("hakina:urgent-refresh", onRefresh);
    window.addEventListener("hakina:submission-status-changed", onRefresh);
    window.addEventListener("online", onRefresh);
    window.addEventListener("hakina:submission-viewed", onViewed);
    return () => {
      window.removeEventListener("hakina:urgent-refresh", onRefresh);
      window.removeEventListener("hakina:submission-status-changed", onRefresh);
      window.removeEventListener("online", onRefresh);
      window.removeEventListener("hakina:submission-viewed", onViewed);
    };
  }, [load, readSeen]);

  const resolve = async (id) => {
    // navigator.onLine may inform, never gate — attempt and let a real failure
    // surface the honest message rather than blocking on an unreliable signal.
    const { error } = await resolveSubmission(supabase, id);
    if (error) {
      showToast("Status changes need a connection.");
      return;
    }
    showToast("Marked resolved.");
    load();
  };

  const filtered = rows.filter((s) =>
    chip === "all" ? true : chip === "flagged" ? s.flagged : !s.flagged);
  const sorted = oldestFirst ? [...filtered].reverse() : filtered;

  if (loading) {
    return <Flex minH="40vh" align="center" justify="center"><Spinner size="lg" color={colors.teal} borderWidth="3px" /></Flex>;
  }

  return (
    <Box data-testid="urgent-queue-page">
      <Flex justify="space-between" align="flex-start" flexWrap="wrap" gap="3" mb="1">
        <Box>
          <Text as="h1" fontFamily="heading" fontWeight="700" fontSize={{ base: "2xl", md: "3xl" }} color={colors.textPrimary}>
            Urgent Items
          </Text>
          <Text mt="1" fontSize={{ base: "sm", md: "md" }} color={colors.textSecondary} maxW="640px">
            Every unresolved flagged report and open action request — triage oldest first and resolve as you go.
          </Text>
        </Box>
        <Text fontSize="sm" fontWeight="700" color={colors.poppy} mt="2" data-testid="urgent-queue-count">
          {rows.length} open
        </Text>
      </Flex>

      {offline && (
        <Box mt="3" px="4" py="3" borderRadius="10px" bg={colors.pendingBg} color={colors.pendingText} fontSize="sm" data-testid="urgent-queue-offline-banner">
          You're offline — showing the last saved queue. Resolving needs a connection.
        </Box>
      )}

      <Flex mt="4" mb="4" gap="2" flexWrap="wrap" align="center">
        {CHIPS.map((c) => (
          <Button key={c.key} size="xs" borderRadius="full" px="3"
            variant={chip === c.key ? "solid" : "outline"}
            bg={chip === c.key ? colors.teal : "transparent"}
            color={chip === c.key ? "white" : colors.textPrimary}
            borderColor={colors.border}
            onClick={() => setChip(c.key)} data-testid={`urgent-chip-${c.key}`}>
            {c.label}
          </Button>
        ))}
        <Button size="xs" variant="ghost" ml="auto" color={colors.textSecondary}
          onClick={() => setOldestFirst((v) => !v)} data-testid="urgent-sort-toggle">
          {oldestFirst ? "Oldest first ↑" : "Newest first ↓"}
        </Button>
      </Flex>

      {sorted.length === 0 ? (
        <Box py="14" textAlign="center" bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="14px" data-testid="urgent-queue-empty">
          <Icon as={CheckCircle2} boxSize="7" color={colors.sageDeep} />
          <Text mt="2" fontSize="sm" color={colors.textSecondary}>All clear — nothing urgent is open right now.</Text>
        </Box>
      ) : (
        <Stack gap="2">
          {sorted.map((s) => (
            <QueueRow key={s.id} s={s} isSeen={seen.has(s.id)} offline={offline}
              onOpen={() => navigate(`/submissions/${s.id}`)} onResolve={() => resolve(s.id)} />
          ))}
        </Stack>
      )}
    </Box>
  );
}

const QueueRow = ({ s, isSeen, offline, onOpen, onResolve }) => {
  const [armed, setArmed] = useState(false);
  const isFlag = s.flagged;
  return (
    <Flex gap="3" px={{ base: "3", md: "4" }} py="3" bg={colors.white} border="1px solid"
      borderColor={colors.border} borderRadius="14px" align="center" flexWrap="wrap"
      data-testid={`urgent-row-${s.id}`}>
      <Flex align="center" justify="center" w="34px" h="34px" borderRadius="9px" flexShrink={0}
        bg={isFlag ? colors.criticalBg : colors.poppyTint}>
        <Icon as={isFlag ? Flag : Link2} boxSize="4" color={colors.poppy} />
      </Flex>
      <Box minW="0" flex="1 1 200px">
        <HStack gap="2">
          <Text fontSize="sm" fontWeight="600" color={colors.textPrimary} lineClamp={1}>
            {isFlag ? `Flagged: ${s.template_title}` : `${s.template_type} raised`}
          </Text>
          {!isSeen && <Box w="8px" h="8px" borderRadius="full" bg={colors.poppy} flexShrink={0} data-testid={`urgent-unread-${s.id}`} />}
        </HStack>
        <Text fontSize="xs" color={colors.textSecondary} lineClamp={1}>
          {s.worker_name} · {s.location_name}
          {isFlag && s.flag_reasons?.length ? ` · ${s.flag_reasons[0]}` : ""}
          {!isFlag && s.link_context?.parent_title ? ` · from ${s.link_context.parent_title}` : ""}
        </Text>
        <Text fontSize="10px" fontWeight="600" color={colors.poppy}>{ago(s.created_at)}</Text>
      </Box>
      <HStack gap="2" flexShrink={0} ml="auto">
        <Button size="xs" variant="outline" borderColor={colors.border} color={colors.textPrimary}
          onClick={onOpen} data-testid={`urgent-open-${s.id}`}>
          <ArrowUpRight size={13} /> Open
        </Button>
        <Button size="xs" variant={armed ? "solid" : "outline"}
          bg={armed ? colors.sageDeep : "transparent"} color={armed ? "white" : colors.sageDeep}
          borderColor={colors.sageDeep} disabled={offline}
          onClick={() => { if (armed) { setArmed(false); onResolve(); } else { setArmed(true); setTimeout(() => setArmed(false), 3500); } }}
          data-testid={`urgent-resolve-${s.id}`}>
          <CheckCircle2 size={13} /> {armed ? "Confirm resolve" : "Resolve"}
        </Button>
      </HStack>
    </Flex>
  );
};
